<!--
    NavBar component is the navigation bar of the app (seems legit)
-->
<script setup lang="ts">
/* UTILS */
import { useCssBreakpoints } from '@af-lib/vue-ui';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

/* STORES */
import { useOverlayStore } from '@/components/drawers/overlayStore';
import LanguageSelector from '@/features/language-selector/LanguageSelector.vue';
import MobileNavDrawer from '@/features/navbar/MobileNavDrawer.vue';
import NavBarDevMenu from '@/features/navbar/NavBarDevMenu.vue';
import NavBarSearch from '@/features/navbar/NavBarSearch.vue';
/* COMPONENTS */
import NavMenu from '@/features/navbar/NavMenu.vue';
import NavTrialDays from '@/features/navbar/NavTrialDays.vue';
import UserMenu from '@/features/navbar/UserMenu.vue';
import { isFreemiumOrTrial } from '@/helpers/users';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';

import BatchesDrawer from './batches/BatchesDrawer.vue';
import BatchesMenu from './batches/BatchesMenu.vue';
import InviteContactBtn from './InviteContactBtn.vue';
import NotificationMenu from './NotificationMenu.vue';
import { ALIconButton } from '@af-lib/vue-ui';

/* UTILS */
const overlayStore = useOverlayStore();
const globalStore = useGlobalStore();
const authStore = useAuthStore();
const { tabletAndUp } = useCssBreakpoints();

const hasInvitePermission = authStore.hasPermission('invite_cta');

const isTrial = authStore.getCompany.is_trial;

/* COMPUTED */
const getBeamer = computed(() => !isFreemiumOrTrial() && globalStore.environment === 'prod');

/* FUNCTIONS */
/**
 * On hamburger click, display the mobile side panel
 */
function openMobileMenu() {
  overlayStore.toggle('nav');
}
</script>

<template>
  <v-app-bar class="navbar" elevation="0" role="banner">
    <!-- align is 'align-items' props -->
    <v-row
      align="center"
      class="px-5 flex-nowrap gap-0"
      tag="nav"
      role="navigation"
      :aria-label="$t('accessibility.menu.main')"
    >
      <!-- logo -->
      <RouterLink :to="{ name: 'network' }" class="me-5" :title="$t('accessibility.link.home')">
        <v-img
          :role="undefined"
          :alt="$t('accessibility.logo.logo')"
          :aria-label="undefined"
          :src="$staticAssets('af-logo.png')"
          width="48px"
        />
      </RouterLink>

      <template v-if="tabletAndUp">
        <NavMenu class="me-5" />
        <NavTrialDays v-if="isTrial" />
      </template>

      <!-- will add an empty element to fill the space -->
      <v-spacer />

      <div class="d-flex align-center" style="column-gap: 20px">
        <template v-if="tabletAndUp">
          <NavBarDevMenu v-if="globalStore.getEnvironment === 'dev' && globalStore?.getDevUsers.length" />
          <BatchesMenu />
          <NavBarSearch />
          <InviteContactBtn v-if="hasInvitePermission" />
          <NotificationMenu />
          <ALIconButton
            v-if="getBeamer"
            id="beamer-notification"
            icon="fas fa-bullhorn"
            variant="tertiary"
            :label="$t('accessibility.notification.updates')"
            :shouldTeleportTooltip="true"
          />
          <LanguageSelector />
          <UserMenu />
        </template>
        <template v-else>
          <LanguageSelector />
          <NotificationMenu />
          <ALIconButton
            v-if="getBeamer"
            id="beamer-notification"
            icon="fas fa-bullhorn"
            variant="tertiary"
            :label="$t('accessibility.notification.updates')"
            :shouldTeleportTooltip="true"
          />

          <ALIconButton
            icon="fa-bars"
            variant="tertiary"
            :label="$t('accessibility.menu.open')"
            :shouldTeleportTooltip="true"
            @click="openMobileMenu"
          />
        </template>
      </div>
    </v-row>
  </v-app-bar>
  <!-- Navbar Drawers -->
  <Teleport to="#af-app">
    <MobileNavDrawer />
    <BatchesDrawer />
  </Teleport>
</template>

<style scoped lang="scss">
.navbar {
  border-bottom: 1px solid rgb(var(--v-theme-border));
  z-index: 1000 !important;
}
</style>
